import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  DollarSign, 
  FileText, 
  CheckCircle, 
  TrendingUp,
  Clock,
  AlertCircle,
  Download,
  Eye,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus
} from 'lucide-react';

interface ProjectEstimateProps {
  projectEstimate: {
    status: string;
    projectPlan: {
      id: string;
      status: string;
      generatedAt: string;
      individualWorkItems: any[];
      projectCoordination: {
        projectTotals: {
          workItemsTotalLow: number;
          workItemsTotalHigh: number;
          overheadLow: number;
          overheadHigh: number;
          grandTotalLow: number;
          grandTotalHigh: number;
        };
        overheadAnalysis: {
          percentage: number;
          reasoning: string;
        };
      };
      confidenceAnalysis: {
        overallConfidence: number;
        qualityAssessment: string;
        notes: string;
      };
    };
    summary: {
      totalWorkItems: number;
      completedWorkItems: number;
      estimatedCostRange: { low: number; high: number };
      confidence: number;
    };
  };
  onViewDetails?: () => void;
  onDownloadEstimate?: () => void;
}

export const ProjectEstimate: React.FC<ProjectEstimateProps> = ({ 
  projectEstimate,
  onViewDetails,
  onDownloadEstimate 
}) => {
  const { projectPlan, summary } = projectEstimate;
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600 bg-green-50';
    if (confidence >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 80) return CheckCircle;
    if (confidence >= 60) return Clock;
    return AlertCircle;
  };

  const ConfidenceIcon = getConfidenceIcon(summary.confidence);

  return (
    <Card className="border-green-200 bg-green-50/30">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Project Estimate Generated
          </div>
          <Badge variant="outline" className="text-green-700 border-green-300">
            Loan Ready
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Cost Range - Primary Display */}
        <div className="text-center p-6 bg-white rounded-lg border border-green-200">
          <div className="text-sm text-gray-600 mb-2">Estimated Project Cost</div>
          <div className="text-3xl font-bold text-green-600 mb-2">
            {formatCurrency(summary.estimatedCostRange.low)} - {formatCurrency(summary.estimatedCostRange.high)}
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-3">
            <ConfidenceIcon className={`h-4 w-4 ${summary.confidence >= 80 ? 'text-green-600' : summary.confidence >= 60 ? 'text-yellow-600' : 'text-red-600'}`} />
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(summary.confidence)}`}>
              {summary.confidence}% Confidence
            </span>
          </div>
          
          {(() => {
            const totalVariance = Math.round((summary.estimatedCostRange.high / summary.estimatedCostRange.low - 1) * 100);
            if (totalVariance > 40) {
              return (
                <div className="text-xs text-left bg-blue-50 p-3 rounded border-l-4 border-blue-300">
                  <div className="font-medium text-blue-800 mb-1">
                    Why the {totalVariance}% cost range?
                  </div>
                  <div className="text-blue-700 space-y-1">
                    <div>• Material quality options (budget vs premium finishes)</div>
                    <div>• Labor complexity varies by existing conditions</div>
                    <div>• Scope adjustments based on discovered issues</div>
                    <div>• Regional pricing variations for contractors</div>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Project Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-white rounded-lg border">
            <div className="text-2xl font-bold text-blue-600">{summary.completedWorkItems}</div>
            <div className="text-sm text-gray-600">Work Items</div>
            <div className="text-xs text-gray-500">Analyzed</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg border">
            <div className="text-2xl font-bold text-gray-600">
              {formatCurrency(projectPlan.projectCoordination.projectTotals.workItemsTotalLow)}
            </div>
            <div className="text-sm text-gray-600">Base Cost</div>
            <div className="text-xs text-gray-500">Materials + Labor</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg border">
            <div className="text-2xl font-bold text-orange-600">
              {projectPlan.projectCoordination.overheadAnalysis.percentage}%
            </div>
            <div className="text-sm text-gray-600">Overhead</div>
            <div className="text-xs text-gray-500">Permits + Coordination</div>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Cost Breakdown
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Work Items Total:</span>
              <span className="font-medium">
                {formatCurrency(projectPlan.projectCoordination.projectTotals.workItemsTotalLow)} - {formatCurrency(projectPlan.projectCoordination.projectTotals.workItemsTotalHigh)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Project Overhead ({projectPlan.projectCoordination.overheadAnalysis.percentage}%):</span>
              <span className="font-medium">
                {formatCurrency(projectPlan.projectCoordination.projectTotals.overheadLow)} - {formatCurrency(projectPlan.projectCoordination.projectTotals.overheadHigh)}
              </span>
            </div>
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total Project Cost:</span>
              <span className="text-green-600">
                {formatCurrency(projectPlan.projectCoordination.projectTotals.grandTotalLow)} - {formatCurrency(projectPlan.projectCoordination.projectTotals.grandTotalHigh)}
              </span>
            </div>
          </div>
        </div>

        {/* Estimate Quality */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Estimate Quality: {projectPlan.confidenceAnalysis.qualityAssessment}
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            {projectPlan.confidenceAnalysis.notes}
          </p>
          <div className="text-xs text-gray-500">
            Generated on {new Date(projectPlan.generatedAt).toLocaleDateString()} • 
            Overhead reasoning: {projectPlan.projectCoordination.overheadAnalysis.reasoning}
          </div>
        </div>

        {/* Work Items Breakdown - Enhanced */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h4 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Work Items Breakdown
            </h4>
            <div className="text-xs text-gray-500 mt-1">
              Click on items to see detailed cost breakdown
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-1">
              {projectPlan.individualWorkItems.map((item, index) => {
                const itemKey = `work-item-${index}`;
                const isExpanded = expandedItems[itemKey];
                const totalVariance = Math.round((item.totalCostHigh / item.totalCostLow - 1) * 100);
                const valueRange = item.valueImpact ? `${item.valueImpact.lowPercent}%-${item.valueImpact.highPercent}%` : 'N/A';

                return (
                  <div key={item.workItemId} className="border-b last:border-b-0">
                    {/* Collapsible Header */}
                    <div 
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleExpand(itemKey)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-medium text-sm">{item.workItemName}</h5>
                          </div>
                          <div className="text-xs text-gray-500 capitalize">{item.workItemCategory}</div>
                        </div>
                        <div className="text-right mr-4">
                          <div className="font-semibold text-sm text-green-600">
                            {formatCurrency(item.totalCostLow)} - {formatCurrency(item.totalCostHigh)}
                          </div>
                        </div>
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-gray-50">
                        {/* Cost Breakdown - Fxer Style */}
                        <div className="bg-white rounded border mb-3">
                          <div className="p-3 border-b bg-gray-50">
                            <h6 className="font-medium text-sm">Cost Breakdown</h6>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Labor Section */}
                              <div className="space-y-2">
                                <h6 className="font-semibold text-sm">Labor</h6>
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                  <span>{item.laborEstimateLow}-{item.laborEstimateHigh} hours</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <svg className="h-4 w-4 text-gray-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v20m8-10H4"/>
                                  </svg>
                                  <span>Rate: {formatCurrency(Math.round(item.laborCostLow / item.laborEstimateLow))}-{formatCurrency(Math.round(item.laborCostHigh / item.laborEstimateHigh))}/hr</span>
                                </div>
                                <div className="font-semibold text-sm mt-2">
                                  Total Labor: {formatCurrency(item.laborCostLow)} - {formatCurrency(item.laborCostHigh)}
                                </div>
                              </div>

                              {/* Materials Section */}
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <h6 className="font-semibold text-sm">Materials</h6>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => toggleExpand(`materials-${itemKey}`)}
                                    className="h-6 w-6 p-0"
                                  >
                                    {expandedItems[`materials-${itemKey}`] ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                                  </Button>
                                </div>
                                
                                {expandedItems[`materials-${itemKey}`] && item.materials.length > 0 && (
                                  <div className="space-y-2 mt-2 max-h-32 overflow-y-auto">
                                    {item.materials.slice(0, 4).map((material: any, matIndex: number) => (
                                      <div key={matIndex} className="ml-4 text-xs border-l-2 border-gray-200 pl-3">
                                        <span className="font-medium block text-gray-800">{material.description}</span>
                                        <div className="grid grid-cols-1 gap-1 mt-1 text-gray-600">
                                          <span>Quantity: {material.quantityLow}-{material.quantityHigh} {material.unit}</span>
                                          <span>Unit Price: {formatCurrency(material.lowCostPerUnit)}-{formatCurrency(material.highCostPerUnit)}</span>
                                          <span>Total: {formatCurrency(material.lowCostPerUnit * material.quantityLow)} - {formatCurrency(material.highCostPerUnit * material.quantityHigh)}</span>
                                        </div>
                                      </div>
                                    ))}
                                    {item.materials.length > 4 && (
                                      <div className="text-xs text-gray-500 ml-4">+{item.materials.length - 4} more materials</div>
                                    )}
                                  </div>
                                )}
                                
                                <div className="font-semibold text-sm mt-2">
                                  Total Materials: {formatCurrency(item.totalMaterialCostLow)} - {formatCurrency(item.totalMaterialCostHigh)}
                                </div>
                              </div>
                            </div>

                            {/* Total Estimate */}
                            <div className="font-semibold mt-4 pt-3 border-t text-sm">
                              Total Estimate: {formatCurrency(item.totalCostLow)} - {formatCurrency(item.totalCostHigh)}
                            </div>
                          </div>
                        </div>


                        {/* Variance Explanation */}
                        {totalVariance > 50 && (
                          <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                            <div className="text-yellow-800 font-medium text-sm mb-1">
                              Why the {totalVariance}% variance?
                            </div>
                            <div className="text-yellow-700 text-xs space-y-1">
                              <div>• Labor complexity varies by existing conditions</div>
                              <div>• Material quality options (standard vs premium)</div>
                              <div>• Scope adjustments based on discoveries</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {false && (
            <Button 
              onClick={onViewDetails}
              variant="outline" 
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Full Details
            </Button>
          )}
          <Button 
            onClick={onDownloadEstimate}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Download for Lender
          </Button>
        </div>

        <div className="text-xs text-center text-gray-500 bg-blue-50 p-3 rounded-lg">
          💡 This estimate is designed for loan pre-qualification. Share with your lender to determine renovation loan eligibility.
        </div>
      </CardContent>
    </Card>
  );
};